import type { Maintenance, PortalLicense } from '@central/api';
import {
  Card,
  PageHeader,
  StatusChip,
  color,
  daysUntil,
  downloadText,
  formatDate,
  status,
  InfoRow,
} from '@central/ui';
import { Alert, Button, VisuallyHidden } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { api, licensesQuery } from '../api';
import { ComposeButton } from '../components/ComposeButton';
import { Mono } from '../components/Mono';
import { RequestRenewal } from '../components/RequestRenewal';
import { QueryState } from '../components/QueryState';
import { errorMessage } from '../format';

export function Licenses() {
  const { data, error } = useQuery(licensesQuery);
  return (
    <>
      <PageHeader
        title="Licenses"
        subtitle="What you’re licensed for, and how much of it each deployment uses."
        actions={
          <ComposeButton kind="license" subject="License change" primary>
            Request a change
          </ComposeButton>
        }
      />
      {!data ? (
        <QueryState error={error} />
      ) : data.items.length === 0 ? (
        <Card>Your organization has no licenses yet.</Card>
      ) : (
        data.items.map((l) => <LicenseCard key={l.id} l={l} maintenance={data.maintenance} />)
      )}
    </>
  );
}

function StatusBadge({ l }: { l: PortalLicense }) {
  if (l.status === 'suspended') return <StatusChip tone="down">Suspended</StatusChip>;
  if (l.status === 'expired') return <StatusChip tone="down">Expired</StatusChip>;
  if (l.status === 'expiring') {
    const d = daysUntil(l.expiresAt);
    return (
      <StatusChip tone="warn">
        Expires in {d} day{d === 1 ? '' : 's'}
      </StatusChip>
    );
  }
  return <StatusChip tone="ok">Active</StatusChip>;
}

/** "2 of 3 · Production, Staging": seats held, then the deployments on the license. */
function activationsText(l: PortalLicense): string {
  const a = l.activations;
  const count =
    a.allowed < 0
      ? `${a.active} · unlimited`
      : a.allowed === 0
        ? 'Offline only'
        : `${a.active} of ${a.allowed}`;
  const names = l.deployments.map((d) => d.name).join(', ');
  return names ? `${count} · ${names}` : count;
}

function LicenseCard({ l, maintenance }: { l: PortalLicense; maintenance: Maintenance }) {
  const quantities = l.features.filter((f) => f.isQuantity);
  const modules = l.features.filter((f) => !f.isQuantity);
  const title = l.deployments.length
    ? `${l.deployments.map((d) => d.name).join(', ')} license`
    : 'License';
  const days = daysUntil(l.expiresAt);
  const renewable = l.status === 'expiring' || l.status === 'expired';
  return (
    <section
      aria-label={title}
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
            <StatusBadge l={l} />
          </div>
          {renewable && <RequestRenewal licenseId={l.id} />}
        </div>
        <LicenseKey l={l} />
        {l.status === 'suspended' && (
          <Alert color="red" variant="light">
            Rethink has suspended this license. Deployments using it stop renewing and run in their
            grace period. Send us a message to sort it out.
          </Alert>
        )}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
          gap: '0 32px',
        }}
      >
        <InfoRow label="Expires">
          {formatDate(l.expiresAt)}
          {days >= 0 && ` · ${days} day${days === 1 ? '' : 's'}`}
        </InfoRow>
        {maintenance.endsAt && (
          <InfoRow label="Maintenance through">{formatDate(maintenance.endsAt)}</InfoRow>
        )}
        <InfoRow label="Grace period">
          {l.graceDays} day{l.graceDays === 1 ? '' : 's'}
        </InfoRow>
        <InfoRow label="Activations">{activationsText(l)}</InfoRow>
      </div>
      {quantities.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h3 style={{ margin: 0, fontSize: 14, color: color.textBody }}>
            {quantities.some((f) => f.used !== undefined) ? 'Seats used' : 'Seats'}
          </h3>
          {quantities.map((f) => (
            <Seat key={f.key} f={f} />
          ))}
        </div>
      )}
      {modules.length + l.unlicensedModules.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, color: color.textBody }}>Modules</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {modules.map((f) => (
              <ModuleTag key={f.key} name={f.name} />
            ))}
            {l.unlicensedModules.map((f) => (
              <ModuleTag key={f.key} name={f.name} unlicensed />
            ))}
          </div>
        </div>
      )}
      {l.offlineFile && <OfflineFile l={l} />}
    </section>
  );
}

/** A module on the license; one it does not grant is struck through. */
function ModuleTag({ name, unlicensed = false }: { name: string; unlicensed?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 28,
        padding: '0 10px',
        borderRadius: 6,
        fontSize: 13,
        background: unlicensed ? color.page : color.portalNavActiveBg,
        color: unlicensed ? color.textMuted : color.textBody,
        textDecoration: unlicensed ? 'line-through' : undefined,
      }}
    >
      {name}
      {unlicensed && <VisuallyHidden>, not licensed</VisuallyHidden>}
    </span>
  );
}

/** Owners download the offline license file for the license's air-gapped machines. */
function OfflineFile({ l }: { l: PortalLicense }) {
  const { me } = useRouteContext({ from: '/app' });
  const download = useMutation({
    mutationFn: () => api.licenses.offlineFile(l.id),
    onSuccess: (f) => downloadText(f.filename ?? `${l.id}.license`, f.text),
  });
  if (me.user.role !== 'Owner') return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
      <div>
        <Button
          variant="default"
          size="sm"
          loading={download.isPending}
          onClick={() => download.mutate()}
        >
          Download the offline license file
        </Button>
      </div>
      <span style={{ color: color.textMuted }}>
        Running air-gapped? Load this file in GoATMS under System › License. It’s tied to the
        machine IDs listed on it and needs replacing at each renewal.
      </span>
      {download.error && (
        <Alert color="red" variant="light">
          {errorMessage(download.error)}
        </Alert>
      )}
    </div>
  );
}

/** A quantity feature: licensed amount, plus usage when every deployment shows seats. */
function Seat({ f }: { f: PortalLicense['features'][number] }) {
  const unit = f.unit ? ` ${f.unit}` : '';
  if (f.used === undefined) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
        <span style={{ fontWeight: 600 }}>{f.name}</span>
        <span>
          <Mono>{f.quantity}</Mono>
          {unit} licensed
        </span>
      </div>
    );
  }
  const pct = f.quantity > 0 ? Math.round((f.used / f.quantity) * 100) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
        <span style={{ fontWeight: 600 }}>{f.name}</span>
        <span>
          <Mono>
            {f.used} / {f.quantity}
          </Mono>{' '}
          <span style={{ color: color.textMuted }}>{pct}%</span>
        </span>
      </div>
      <div
        role="img"
        aria-label={`${f.name} ${pct}% used`}
        style={{ height: 8, background: color.rowRule, borderRadius: 4, display: 'flex' }}
      >
        <div
          style={{
            width: `${Math.min(pct, 100)}%`,
            background: pct >= 90 ? status.warn.fg : color.link,
            borderRadius: 4,
          }}
        />
      </div>
    </div>
  );
}

/** Masked key; an Owner can reveal and copy the full key. */
function LicenseKey({ l }: { l: PortalLicense }) {
  const { me } = useRouteContext({ from: '/app' });
  const [copied, setCopied] = useState(false);
  const reveal = useMutation({ mutationFn: () => api.licenses.reveal(l.id) });
  const key = reveal.data?.key;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span
          data-testid="license-key"
          style={{
            fontFamily: 'var(--mantine-font-family-monospace)',
            fontSize: 15,
            background: color.page,
            borderRadius: 6,
            padding: '6px 10px',
            wordBreak: 'break-all',
          }}
        >
          {key ?? l.keyMasked}
        </span>
        {me.user.role === 'Owner' && !key && (
          <Button
            variant="default"
            size="sm"
            loading={reveal.isPending}
            onClick={() => reveal.mutate()}
          >
            Reveal
          </Button>
        )}
        {key && (
          <Button
            variant="default"
            size="sm"
            onClick={() =>
              void navigator.clipboard?.writeText(key).then(
                () => setCopied(true),
                () => setCopied(false),
              )
            }
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </div>
      {reveal.error && (
        <Alert color="red" variant="light">
          {errorMessage(reveal.error)}
        </Alert>
      )}
    </div>
  );
}
